import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  db,
  storage,
  collection,
  query,
  where,
  orderBy,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  listAll,
  onSnapshot,
  writeBatch,
  getDocs,
  getDoc,
  callFunction,
} from '@/lib/firebase';
import { identifyWineFromImage, identifyWineFromStorageUrls, type GeminiWineRecognition } from '@/lib/firebase';
import imageCompression from 'browser-image-compression';
import type { Wine, WineFormData, WineFilters, WineStats, SharedAccess, ShareAccessType, ShareRequest, UserConnection, WineLocation } from '@/types';
import { useAuth } from './AuthContext';
import { normalizeTextField } from '@/lib/textUtils';

interface WinesContextType {
  wines: Wine[];
  loading: boolean;
  error: string | null;
  addWine: (wineData: WineFormData, imageFiles?: File[]) => Promise<string>;
  updateWine: (wineId: string, wineData: Partial<WineFormData>, imageFiles?: (File | string)[]) => Promise<void>;
  deleteWine: (wineId: string) => Promise<void>;
  markAsConsumed: (wineId: string, rating?: number, notes?: string) => Promise<void>;
  incrementWineQuantity: (wineId: string) => Promise<void>;
  identifyWineFromPhoto: (imageFiles: File | File[], existingData?: Partial<GeminiWineRecognition>) => Promise<GeminiWineRecognition[]>;
  identifyWineFromPhotoUrls: (imageUrls: string[], existingData?: Partial<GeminiWineRecognition>) => Promise<GeminiWineRecognition[]>;
  getWineStats: () => WineStats;
  filterWines: (filters: WineFilters) => Wine[];
  // Funzioni condivisione vini (nuovo sistema con connessioni)
  connectedUsers: UserConnection[];
  sentInvites: UserConnection[];
  receivedInvites: UserConnection[];
  generateConnectionCode: (inviteeEmail: string) => Promise<string>;
  acceptConnectionCode: (code: string) => Promise<void>;
  acceptInvite: (connectionId: string) => Promise<void>;
  rejectInvite: (connectionId: string) => Promise<void>;
  cancelInvite: (connectionId: string) => Promise<void>;
  removeConnection: (connectionId: string) => Promise<void>;
  shareWinesWithUsers: (wineIds: string[], userIds: string[], type: ShareAccessType) => Promise<void>;
  // Funzioni condivisione (vecchio sistema - DEPRECATED)
  shareWines: (wineIds: string[], targetUserId: string, targetUserName: string, type: ShareAccessType) => Promise<void>;
  unshareWine: (wineId: string, targetUserId: string) => Promise<void>;
  updateShareAccessType: (wineId: string, targetUserId: string, newType: ShareAccessType) => Promise<void>;
  generateShareCode: (wineIds: string[], type: ShareAccessType) => Promise<string>;
  acceptShareCode: (code: string) => Promise<void>;
  // Funzioni permessi
  canModifyWine: (wine: Wine) => boolean;
  canDeleteWine: (wine: Wine) => boolean;
  canViewWine: (wine: Wine) => boolean;
  isOwner: (wine: Wine) => boolean;
  // Gestione locazioni
  locations: WineLocation[];
  locationsLoading: boolean;
  createLocation: (name: string, description?: string) => Promise<string>;
  updateLocation: (locationId: string, data: Partial<Pick<WineLocation, 'name' | 'description' | 'order'>>) => Promise<void>;
  deleteLocation: (locationId: string) => Promise<void>;
}

const WinesContext = createContext<WinesContextType | undefined>(undefined);

export const useWines = () => {
  const context = useContext(WinesContext);
  if (context === undefined) {
    throw new Error('useWines must be used within a WinesProvider');
  }
  return context;
};

interface WinesProviderProps {
  children: React.ReactNode;
}

export const WinesProvider: React.FC<WinesProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [wines, setWines] = useState<Wine[]>([]);
  const [connectedUsers, setConnectedUsers] = useState<UserConnection[]>([]);
  const [sentInvites, setSentInvites] = useState<UserConnection[]>([]);
  const [receivedInvites, setReceivedInvites] = useState<UserConnection[]>([]);
  const [locations, setLocations] = useState<WineLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener per i vini dell'utente (proprietà + condivisi)
  useEffect(() => {
    if (!user) {
      setWines([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Query 1: Vini di cui sono proprietario (userId per backward compatibility)
    const myWinesQuery = query(
      collection(db, 'wines'),
      where('userId', '==', user.id),
      orderBy('createdAt', 'desc')
    );

    // Query 2: Vini condivisi con me (array-contains su sharedWithUserIds)
    const sharedWinesQuery = query(
      collection(db, 'wines'),
      where('sharedWithUserIds', 'array-contains', user.id),
      orderBy('createdAt', 'desc')
    );

    // Map per tracciare vini già aggiunti (evita duplicati se un vino è sia owned che shared)
    const winesMap = new Map<string, Wine>();

    // Listener 1: I miei vini
    const unsubscribeMyWines = onSnapshot(
      myWinesQuery,
      (snapshot) => {
        // Gestisci aggiunte, modifiche e rimozioni
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'removed') {
            // Rimuovi vino eliminato dalla map
            winesMap.delete(change.doc.id);
          } else {
            // Aggiungi o aggiorna vino (added/modified)
            const data = change.doc.data();
            winesMap.set(change.doc.id, {
              id: change.doc.id,
              ...data,
              ownerId: data.ownerId || data.userId,
              isShared: data.isShared || false,
              sharedWith: data.sharedWith || [],
              sharedWithUserIds: data.sharedWithUserIds || [],
              fullAccessUserIds: data.fullAccessUserIds || [],
            } as Wine);
          }
        });

        // Aggiorna stato con tutti i vini
        setWines(Array.from(winesMap.values()).sort((a, b) => {
          const aTime = a.createdAt?.seconds ?? 0;
          const bTime = b.createdAt?.seconds ?? 0;
          return bTime - aTime;
        }));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching my wines:', err);
        setError('Errore nel caricamento dei vini');
        setLoading(false);
      }
    );

    // Listener 2: Vini condivisi con me
    const unsubscribeSharedWines = onSnapshot(
      sharedWinesQuery,
      (snapshot) => {
        // Gestisci aggiunte, modifiche e rimozioni
        snapshot.docChanges().forEach((change) => {
          if (change.type === 'removed') {
            // Rimuovi vino eliminato dalla map
            winesMap.delete(change.doc.id);
          } else {
            // Aggiungi o aggiorna vino (added/modified)
            const data = change.doc.data();
            winesMap.set(change.doc.id, {
              id: change.doc.id,
              ...data,
              ownerId: data.ownerId || data.userId,
              isShared: data.isShared || false,
              sharedWith: data.sharedWith || [],
              sharedWithUserIds: data.sharedWithUserIds || [],
              fullAccessUserIds: data.fullAccessUserIds || [],
            } as Wine);
          }
        });

        // Aggiorna stato con tutti i vini
        setWines(Array.from(winesMap.values()).sort((a, b) => {
          const aTime = a.createdAt?.seconds ?? 0;
          const bTime = b.createdAt?.seconds ?? 0;
          return bTime - aTime;
        }));
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching shared wines:', err);
        // Non bloccare l'app se la query dei vini condivisi fallisce
        setLoading(false);
      }
    );

    return () => {
      unsubscribeMyWines();
      unsubscribeSharedWines();
    };
  }, [user]);

  // Real-time listener per le connessioni utente (accettate)
  useEffect(() => {
    if (!user) {
      setConnectedUsers([]);
      setSentInvites([]);
      setReceivedInvites([]);
      return;
    }

    // Query 1: Connessioni accettate dove sono userA
    const connectionsAsAQuery = query(
      collection(db, 'userConnections'),
      where('userA', '==', user.id),
      where('status', '==', 'accepted')
    );

    // Query 2: Connessioni accettate dove sono userB
    const connectionsAsBQuery = query(
      collection(db, 'userConnections'),
      where('userB', '==', user.id),
      where('status', '==', 'accepted')
    );

    // Query 3: Inviti inviati (pending)
    const sentInvitesQuery = query(
      collection(db, 'userConnections'),
      where('userA', '==', user.id),
      where('status', '==', 'pending')
    );

    // Query 4: Inviti ricevuti (pending) - basato su inviteeEmail
    const receivedInvitesQuery = query(
      collection(db, 'userConnections'),
      where('inviteeEmail', '==', user.email.toLowerCase()),
      where('status', '==', 'pending')
    );

    // Usa stati temporanei per ogni query e poi combina
    let connectionsAsA: UserConnection[] = [];
    let connectionsAsB: UserConnection[] = [];

    const updateConnectedUsers = () => {
      const connectionsMap = new Map<string, UserConnection>();

      // Aggiungi connessioni dove sono userA
      connectionsAsA.forEach(conn => {
        connectionsMap.set(conn.id, conn);
      });

      // Aggiungi connessioni dove sono userB
      connectionsAsB.forEach(conn => {
        connectionsMap.set(conn.id, conn);
      });

      setConnectedUsers(Array.from(connectionsMap.values()));
    };

    const unsubscribeAsA = onSnapshot(
      connectionsAsAQuery,
      (snapshot) => {
        connectionsAsA = [];
        snapshot.forEach((doc) => {
          connectionsAsA.push({
            id: doc.id,
            ...doc.data(),
          } as UserConnection);
        });
        updateConnectedUsers();
      },
      (err) => {
        console.error('Error fetching connections as A:', err);
      }
    );

    const unsubscribeAsB = onSnapshot(
      connectionsAsBQuery,
      (snapshot) => {
        connectionsAsB = [];
        snapshot.forEach((doc) => {
          connectionsAsB.push({
            id: doc.id,
            ...doc.data(),
          } as UserConnection);
        });
        updateConnectedUsers();
      },
      (err) => {
        console.error('Error fetching connections as B:', err);
      }
    );

    const unsubscribeSent = onSnapshot(
      sentInvitesQuery,
      (snapshot) => {
        const invites: UserConnection[] = [];
        snapshot.forEach((doc) => {
          invites.push({
            id: doc.id,
            ...doc.data(),
          } as UserConnection);
        });
        setSentInvites(invites);
      },
      (err) => {
        console.error('Error fetching sent invites:', err);
      }
    );

    const unsubscribeReceived = onSnapshot(
      receivedInvitesQuery,
      (snapshot) => {
        const invites: UserConnection[] = [];
        snapshot.forEach((doc) => {
          invites.push({
            id: doc.id,
            ...doc.data(),
          } as UserConnection);
        });
        setReceivedInvites(invites);
      },
      (err) => {
        console.error('Error fetching received invites:', err);
      }
    );

    return () => {
      unsubscribeAsA();
      unsubscribeAsB();
      unsubscribeSent();
      unsubscribeReceived();
    };
  }, [user]);

  // Real-time listener per le locazioni dell'utente
  useEffect(() => {
    if (!user) {
      setLocations([]);
      setLocationsLoading(false);
      return;
    }

    setLocationsLoading(true);

    const locationsQuery = query(
      collection(db, 'wineLocations'),
      where('userId', '==', user.id),
      orderBy('order', 'asc')
    );

    const unsubscribe = onSnapshot(
      locationsQuery,
      (snapshot) => {
        const locs: WineLocation[] = [];
        snapshot.forEach((doc) => {
          locs.push({ id: doc.id, ...doc.data() } as WineLocation);
        });
        setLocations(locs);
        setLocationsLoading(false);
      },
      (err) => {
        console.error('Error fetching locations:', err);
        setLocationsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // Comprimi immagine prima dell'upload
  const compressImage = async (imageFile: File): Promise<File> => {
    try {
      const options = {
        maxSizeMB: 0.3, // 300KB max
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: 'image/jpeg',
      };

      const compressedFile = await imageCompression(imageFile, options);
      return compressedFile;
    } catch (error) {
      console.error('Error compressing image:', error);
      return imageFile; // Fallback: usa file originale
    }
  };

  // Upload immagine su Storage
  const uploadWineImage = async (userId: string, wineId: string, imageFile: File): Promise<string> => {
    try {
      // Comprimi immagine
      const compressedImage = await compressImage(imageFile);

      // Path: wines/{userId}/{wineId}/{timestamp}.jpg
      const timestamp = Date.now();
      const storagePath = `wines/${userId}/${wineId}/${timestamp}.jpg`;
      const storageRef = ref(storage, storagePath);

      // Upload
      await uploadBytes(storageRef, compressedImage);

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading wine image:', error);
      throw new Error('Errore durante il caricamento dell\'immagine');
    }
  };

  // Estrai il path da un URL di Firebase Storage
  const extractPathFromStorageUrl = (url: string): string => {
    // URL format: https://firebasestorage.googleapis.com/v0/b/bucket/o/path%2Fto%2Ffile?alt=media&token=xxx
    const match = url.match(/\/o\/([^?]+)/);
    if (!match) throw new Error('Invalid storage URL');
    return decodeURIComponent(match[1]);
  };

  // Copia immagini da un vino a un altro usando Cloud Function (per documenti consumati separati)
  const copyWineImages = async (
    targetWineId: string,
    imageUrls: string[]
  ): Promise<string[]> => {
    // Se non ci sono immagini, ritorna subito
    if (!imageUrls || imageUrls.length === 0) {
      return [];
    }

    try {
      // Chiama la Cloud Function per copiare le immagini server-side
      const copyImagesFn = callFunction<
        { targetWineId: string; imageUrls: string[] },
        { success: boolean; newImageUrls: string[]; copiedCount: number; totalCount: number }
      >('copyWineImages');

      const result = await copyImagesFn({
        targetWineId,
        imageUrls,
      });

      if (result.data.success && result.data.newImageUrls.length > 0) {
        console.log(`✅ ${result.data.copiedCount}/${result.data.totalCount} immagini copiate con successo`);
        return result.data.newImageUrls;
      }

      // Fallback: usa gli URL originali se nessuna copia è riuscita
      console.warn('⚠️ Nessuna immagine copiata. Usando originali come fallback.');
      return imageUrls;
    } catch (error) {
      // In caso di qualsiasi errore, usa gli URL originali
      console.error('Errore generale copia immagini:', error);
      console.warn('⚠️ Usando immagini originali come fallback');
      return imageUrls;
    }
  };

  // Normalizza i dati del form prima del salvataggio
  const normalizeWineData = (data: Partial<WineFormData>): Partial<WineFormData> => {
    return {
      ...data,
      name: normalizeTextField(data.name),
      producer: normalizeTextField(data.producer),
      grape: normalizeTextField(data.grape),
      denomination: normalizeTextField(data.denomination),
      region: normalizeTextField(data.region),
      location: normalizeTextField(data.location),
      // Note e temperature rimangono come inserite (già normalizzate)
    };
  };

  // Aggiungi nuovo vino
  const addWine = async (wineData: WineFormData, imageFiles?: File[]): Promise<string> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Normalizza i dati testuali
      const normalizedData = normalizeWineData(wineData);

      // Crea documento con ID generato
      const wineRef = doc(collection(db, 'wines'));
      const wineId = wineRef.id;

      let imageUrls: string[] = [];

      // Upload immagini se presenti (max 5)
      if (imageFiles && imageFiles.length > 0) {
        const uploadPromises = imageFiles.map((file) =>
          uploadWineImage(user.id, wineId, file)
        );
        imageUrls = await Promise.all(uploadPromises);
      }

      // Rimuovi campi undefined (Firebase non li accetta)
      const cleanedData = Object.fromEntries(
        Object.entries(normalizedData).filter(([_, value]) => value !== undefined)
      );

      // Salva vino
      const newWine = {
        ...cleanedData,
        userId: user.id,          // Backward compatibility
        ownerId: user.id,         // Nuovo campo proprietario
        isShared: false,          // Inizialmente non condiviso
        sharedWith: [],           // Array vuoto
        sharedWithUserIds: [],    // Array vuoto per query
        fullAccessUserIds: [],    // Array vuoto per accessi full
        status: 'cellar' as const,
        quantity: cleanedData.quantity || 1, // Default 1 se non specificato
        ...(imageUrls.length > 0 && {
          imageUrls,
          imageUrl: imageUrls[0] // Mantieni retrocompatibilità
        }),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(wineRef, newWine);

      return wineId;
    } catch (error) {
      console.error('Error adding wine:', error);
      throw new Error('Errore durante l\'aggiunta del vino');
    }
  };

  // Aggiorna vino esistente
  const updateWine = async (
    wineId: string,
    wineData: Partial<WineFormData>,
    imageFiles?: (File | string)[]
  ): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Normalizza i dati testuali
      const normalizedData = normalizeWineData(wineData);

      const wineRef = doc(db, 'wines', wineId);

      // Verifica permessi
      const wine = wines.find((w) => w.id === wineId);
      if (!wine) {
        throw new Error('Vino non trovato');
      }
      if (!canModifyWine(wine)) {
        throw new Error('Non autorizzato a modificare questo vino');
      }

      let imageUrls = wine.imageUrls || [];
      let shouldUpdateImages = false;

      // Gestisci immagini:
      // - imageFiles === undefined: mantieni immagini esistenti
      // - imageFiles === []: elimina tutte le immagini
      // - imageFiles === [mix di File e string]: mantieni URL esistenti e aggiungi nuove immagini
      if (imageFiles !== undefined) {
        shouldUpdateImages = true;

        // Separa URL esistenti da nuovi File
        const existingUrls = imageFiles.filter((item): item is string => typeof item === 'string');
        const newFiles = imageFiles.filter((item): item is File => item instanceof File);

        // Identifica immagini da eliminare (quelle che non sono più negli URL esistenti)
        const urlsToKeep = new Set(existingUrls);
        const imagesToDelete = (wine.imageUrls || []).filter(url => !urlsToKeep.has(url));

        // Elimina solo le immagini che non sono più presenti
        if (imagesToDelete.length > 0) {
          try {
            for (const url of imagesToDelete) {
              const imagePath = extractPathFromStorageUrl(url);
              const imageRef = ref(storage, imagePath);
              await deleteObject(imageRef);
            }
          } catch (storageError) {
            console.error('Error deleting removed images:', storageError);
          }
        }

        // Upload nuove immagini
        const newImageUrls: string[] = [];
        if (newFiles.length > 0) {
          const uploadPromises = newFiles.map((file) =>
            uploadWineImage(user.id, wineId, file)
          );
          newImageUrls.push(...await Promise.all(uploadPromises));
        }

        // Combina URL esistenti e nuovi URL (nell'ordine)
        imageUrls = [...existingUrls, ...newImageUrls];
      }

      // Gestisci campi che possono essere rimossi esplicitamente
      const updateData: any = {
        updatedAt: serverTimestamp(),
      };

      // Processa i campi del form normalizzati
      Object.entries(normalizedData).forEach(([key, value]) => {
        // Se il valore è undefined, lo ignoriamo (non aggiorniamo il campo)
        if (value === undefined) {
          // Eccezione: alcuni campi possono essere rimossi esplicitamente
          const removableFields = ['rating', 'purchasePrice', 'drinkByYear', 'estimatedValue', 'vintage', 'alcoholContent', 'locationId'];
          if (removableFields.includes(key) && normalizedData.hasOwnProperty(key)) {
            updateData[key] = null; // Imposta a null per rimuovere
          }
          return;
        }
        // Altrimenti, includi il campo
        updateData[key] = value;
      });

      // Aggiorna imageUrls solo se sono state modificate
      if (shouldUpdateImages) {
        if (imageUrls.length > 0) {
          updateData.imageUrls = imageUrls;
          updateData.imageUrl = imageUrls[0]; // Retrocompatibilità
        } else {
          // Rimuovi i campi immagine se non ci sono più immagini
          updateData.imageUrls = [];
          updateData.imageUrl = null;
        }
      }

      await updateDoc(wineRef, updateData);
    } catch (error) {
      console.error('Error updating wine:', error);
      throw new Error('Errore durante l\'aggiornamento del vino');
    }
  };

  // Elimina vino (o rimuove condivisione se non sei il proprietario)
  const deleteWine = async (wineId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const wine = wines.find((w) => w.id === wineId);
      if (!wine) {
        throw new Error('Vino non trovato');
      }
      if (!canDeleteWine(wine)) {
        throw new Error('Non autorizzato a eliminare questo vino');
      }

      const isWineOwner = isOwner(wine);

      // Se NON sei il proprietario, rimuovi solo la condivisione
      if (!isWineOwner) {
        await unshareWine(wineId, user.id);
        console.log(`Removed share for user ${user.id} from wine ${wineId}`);
        return;
      }

      // Se sei il proprietario, elimina completamente il vino
      // Elimina TUTTE le immagini del vino dalla cartella Storage
      try {
        // Usa ownerId per il path corretto delle immagini
        const ownerId = wine.ownerId || wine.userId;
        const folderPath = `wines/${ownerId}/${wineId}`;
        const folderRef = ref(storage, folderPath);

        // Lista tutti i file nella cartella
        const { items } = await listAll(folderRef);

        // Elimina tutti i file
        await Promise.all(items.map(item => deleteObject(item)));

        console.log(`Deleted ${items.length} images from ${folderPath}`);
      } catch (storageError) {
        console.error('Error deleting wine images from storage:', storageError);
        // Non bloccare se l'eliminazione delle immagini fallisce
      }

      // Elimina documento
      await deleteDoc(doc(db, 'wines', wineId));
    } catch (error) {
      console.error('Error deleting wine:', error);
      throw new Error('Errore durante l\'eliminazione del vino');
    }
  };

  // Segna vino come bevuto
  const markAsConsumed = async (wineId: string, rating?: number, notes?: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const wineRef = doc(db, 'wines', wineId);

      const wine = wines.find((w) => w.id === wineId);
      if (!wine) {
        throw new Error('Vino non trovato');
      }

      // Verifica permessi: solo owner o full access possono bere
      if (!canModifyWine(wine)) {
        throw new Error('Non autorizzato a modificare questo vino');
      }

      const currentQuantity = wine.quantity || 1;

      if (currentQuantity > 1) {
        // Quantità > 1: Decrementa e crea nuovo record bevuto
        // 1. Decrementa quantità vino in cantina e rimuovi sempre recensione
        await updateDoc(wineRef, {
          quantity: currentQuantity - 1,
          rating: null,
          consumedNotes: null,
          updatedAt: serverTimestamp(),
        });

        // 2. Crea nuovo record per bottiglia bevuta (sempre personale)
        const consumedWineRef = doc(collection(db, 'wines'));
        const newConsumedWineId = consumedWineRef.id;

        // Copia le immagini nella cartella del nuovo documento consumato
        let copiedImageUrls: string[] = [];
        const originalImageUrls = wine.imageUrls || (wine.imageUrl ? [wine.imageUrl] : []);
        if (originalImageUrls.length > 0) {
          try {
            copiedImageUrls = await copyWineImages(newConsumedWineId, originalImageUrls);
          } catch (error) {
            console.error('Errore copia immagini, uso originali:', error);
            copiedImageUrls = originalImageUrls; // Fallback sicuro
          }
        }

        // Rimuovi campi che non devono essere copiati (id, timestamps, rating/consumedNotes vecchi, condivisione)
        const {
          id: _id,
          createdAt: _createdAt,
          updatedAt: _updatedAt,
          consumedDate: _consumedDate,
          rating: _rating,
          consumedNotes: _consumedNotes,
          imageUrls: _imageUrls,
          imageUrl: _imageUrl,
          sharedWith: _sharedWith,              // Rimuovi condivisione
          isShared: _isShared,                  // Rimuovi condivisione
          sharedWithUserIds: _sharedWithUserIds, // Rimuovi condivisione
          fullAccessUserIds: _fullAccessUserIds, // Rimuovi condivisione
          ownerName: _ownerName,                // Rimuovi owner name cache
          ...wineDataToCopy
        } = wine;

        await setDoc(consumedWineRef, {
          ...wineDataToCopy,
          userId: user.id,              // Vino consumato sempre personale
          ownerId: user.id,             // Proprietario è chi beve
          isShared: false,              // Vini bevuti mai condivisi
          sharedWith: [],               // Array vuoto
          sharedWithUserIds: [],        // Array vuoto per query
          fullAccessUserIds: [],        // Array vuoto per accessi full
          status: 'consumed' as const,
          quantity: 1,
          rating: rating || null,
          consumedNotes: notes || null,
          consumedDate: serverTimestamp(),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          ...(copiedImageUrls.length > 0 && {
            imageUrls: copiedImageUrls,
            imageUrl: copiedImageUrls[0],
          }),
        });
      } else {
        // Quantità = 1: Ultima bottiglia
        if (wine.isShared && wine.sharedWith && wine.sharedWith.length > 0) {
          // Vino condiviso: crea copia personale "consumed" e elimina originale
          const consumedWineRef = doc(collection(db, 'wines'));
          const newConsumedWineId = consumedWineRef.id;

          // Copia le immagini
          let copiedImageUrls: string[] = [];
          const originalImageUrls = wine.imageUrls || (wine.imageUrl ? [wine.imageUrl] : []);
          if (originalImageUrls.length > 0) {
            try {
              copiedImageUrls = await copyWineImages(newConsumedWineId, originalImageUrls);
            } catch (error) {
              console.error('Errore copia immagini, uso originali:', error);
              copiedImageUrls = originalImageUrls;
            }
          }

          // Rimuovi campi che non devono essere copiati
          const {
            id: _id,
            createdAt: _createdAt,
            updatedAt: _updatedAt,
            consumedDate: _consumedDate,
            rating: _rating,
            consumedNotes: _consumedNotes,
            imageUrls: _imageUrls,
            imageUrl: _imageUrl,
            sharedWith: _sharedWith,              // Rimuovi condivisione
            isShared: _isShared,                  // Rimuovi condivisione
            sharedWithUserIds: _sharedWithUserIds, // Rimuovi condivisione
            fullAccessUserIds: _fullAccessUserIds, // Rimuovi condivisione
            ownerName: _ownerName,                // Rimuovi owner name cache
            ...wineDataToCopy
          } = wine;

          // Crea copia personale consumata
          await setDoc(consumedWineRef, {
            ...wineDataToCopy,
            userId: user.id,              // Vino consumato sempre personale
            ownerId: user.id,             // Proprietario è chi beve
            isShared: false,              // Vini bevuti mai condivisi
            sharedWith: [],               // Array vuoto
            sharedWithUserIds: [],        // Array vuoto per query
            fullAccessUserIds: [],        // Array vuoto per accessi full
            status: 'consumed' as const,
            quantity: 1,
            rating: rating || null,
            consumedNotes: notes || null,
            consumedDate: serverTimestamp(),
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            ...(copiedImageUrls.length > 0 && {
              imageUrls: copiedImageUrls,
              imageUrl: copiedImageUrls[0],
            }),
          });

          // Elimina il vino originale (sparisce per tutti)
          // Nota: Non usiamo deleteWine() perché ha controlli permessi diversi.
          // Qui il controllo è già stato fatto con canModifyWine all'inizio.
          await deleteDoc(wineRef);
        } else {
          // Vino non condiviso: normale flusso (marca come bevuto)
          await updateDoc(wineRef, {
            status: 'consumed',
            quantity: 1,
            consumedDate: serverTimestamp(),
            rating: rating || null,
            consumedNotes: notes || null,
            // Rimuovi TUTTA la condivisione (vini bevuti sempre personali)
            isShared: false,
            sharedWith: [],
            sharedWithUserIds: [],
            fullAccessUserIds: [],
            updatedAt: serverTimestamp(),
          });
        }
      }
    } catch (error) {
      console.error('Error marking wine as consumed:', error);
      throw new Error('Errore durante l\'aggiornamento dello stato');
    }
  };

  // Incrementa quantità di un vino (per gestione duplicati)
  const incrementWineQuantity = async (wineId: string): Promise<void> => {
    if (!user) {
      throw new Error('Utente non autenticato');
    }

    try {
      const wine = wines.find(w => w.id === wineId);
      if (!wine) {
        throw new Error('Vino non trovato');
      }

      // Verifica che l'utente sia il proprietario
      if (wine.ownerId !== user.id) {
        throw new Error('Solo il proprietario può incrementare la quantità');
      }

      const wineRef = doc(db, 'wines', wineId);
      const currentQuantity = wine.quantity || 1;

      await updateDoc(wineRef, {
        quantity: currentQuantity + 1,
        updatedAt: serverTimestamp(),
      });

      console.log(`Quantità incrementata per vino ${wineId}: ${currentQuantity} -> ${currentQuantity + 1}`);
    } catch (error) {
      console.error('Error incrementing wine quantity:', error);
      throw new Error('Errore durante l\'incremento della quantità');
    }
  };

  // Riconosci vino da foto usando Gemini AI (supporta foto singola o multiple)
  const identifyWineFromPhoto = async (
    imageFiles: File | File[],
    existingData?: Partial<GeminiWineRecognition>
  ): Promise<GeminiWineRecognition[]> => {
    try {
      return await identifyWineFromImage(imageFiles, existingData);
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Errore durante il riconoscimento del vino. Riprova o inserisci manualmente.');
    }
  };

  // Riconosci vino da URL di Storage (evita CORS scaricando server-side)
  const identifyWineFromPhotoUrls = async (
    imageUrls: string[],
    existingData?: Partial<GeminiWineRecognition>
  ): Promise<GeminiWineRecognition[]> => {
    try {
      return await identifyWineFromStorageUrls(imageUrls, existingData);
    } catch (error) {
      if (error instanceof Error) throw error;
      throw new Error('Errore durante il riconoscimento del vino. Riprova o inserisci manualmente.');
    }
  };

  // Calcola statistiche cantina
  const getWineStats = (): WineStats => {
    const cellarWines = wines.filter((w) => w.status === 'cellar');
    const consumedWines = wines.filter((w) => w.status === 'consumed');

    // Calcola valore totale moltiplicando per quantità - usa purchasePrice se disponibile, altrimenti estimatedValue
    const totalValue = cellarWines.reduce((sum, w) => {
      const price = w.purchasePrice ?? w.estimatedValue ?? 0;
      return sum + price * (w.quantity || 1);
    }, 0);

    const ratedWines = consumedWines.filter((w) => w.rating);
    const averageRating =
      ratedWines.length > 0
        ? ratedWines.reduce((sum, w) => sum + (w.rating || 0), 0) / ratedWines.length
        : 0;

    const winesByType: Record<string, number> = {
      red: 0,
      white: 0,
      rose: 0,
      sparkling: 0,
      dessert: 0,
      fortified: 0,
      orange: 0,
      whisky: 0,
      rum: 0,
      gin: 0,
      grappa: 0,
      cognac: 0,
      liqueur: 0,
      other: 0,
    };

    cellarWines.forEach((wine) => {
      winesByType[wine.type] = (winesByType[wine.type] || 0) + 1;
    });

    const topRatedWines = [...consumedWines]
      .filter((w) => w.rating)
      .sort((a, b) => (b.rating || 0) - (a.rating || 0))
      .slice(0, 5);

    const recentlyAdded = [...wines].sort((a, b) => {
      const aTime = a.createdAt?.seconds ?? 0;
      const bTime = b.createdAt?.seconds ?? 0;
      return bTime - aTime;
    }).slice(0, 5);

    const recentlyConsumed = [...consumedWines]
      .sort((a, b) => (b.consumedDate?.seconds || 0) - (a.consumedDate?.seconds || 0))
      .slice(0, 5);

    return {
      totalWines: wines.length,
      cellarWines: cellarWines.length,
      consumedWines: consumedWines.length,
      totalValue,
      averageRating,
      winesByType: winesByType as any,
      topRatedWines,
      recentlyAdded,
      recentlyConsumed,
    };
  };

  // Filtra vini
  const filterWines = (filters: WineFilters): Wine[] => {
    let filtered = [...wines];

    // Enhanced search - searches across all text fields
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((wine) => {
        const searchableFields = [
          wine.name,
          wine.producer,
          wine.region,
          wine.grape,
          wine.denomination,
          wine.location,
          wine.personalNotes,
          wine.tastingNotes,
          wine.consumedNotes,
          wine.glassType,
          wine.servingTemperature,
          ...(wine.pairings || []),
        ];

        return searchableFields.some(
          (field) => field && field.toLowerCase().includes(searchLower)
        );
      });
    }

    // Filter by type
    if (filters.type && filters.type !== 'all') {
      filtered = filtered.filter((wine) => wine.type === filters.type);
    }

    // Filter by status
    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter((wine) => wine.status === filters.status);
    }

    // Filter by value range
    if (filters.minValue !== undefined) {
      filtered = filtered.filter((wine) => (wine.estimatedValue || 0) >= filters.minValue!);
    }
    if (filters.maxValue !== undefined) {
      filtered = filtered.filter((wine) => (wine.estimatedValue || 0) <= filters.maxValue!);
    }

    // Filter by region
    if (filters.region) {
      filtered = filtered.filter((wine) => wine.region === filters.region);
    }

    // Filter by drink by year (scadenza)
    const currentYear = new Date().getFullYear();
    const anyExpirationFilter = filters.expired || filters.expiringThisYear || filters.expiringNextYear;

    if (anyExpirationFilter) {
      filtered = filtered.filter((wine) => {
        if (!wine.drinkByYear) return false;

        const matchesExpired = filters.expired && wine.drinkByYear < currentYear;
        const matchesThisYear = filters.expiringThisYear && wine.drinkByYear === currentYear;
        const matchesNextYear = filters.expiringNextYear && wine.drinkByYear === currentYear + 1;

        // Mostra il vino se corrisponde a qualsiasi filtro selezionato
        return matchesExpired || matchesThisYear || matchesNextYear;
      });
    }
    // Se nessuna delle tre è selezionata, nessun filtro sulla scadenza

    // Filter by location
    if (filters.locationId) {
      if (filters.locationId === 'none') {
        // Wines without assigned location (locationId not set)
        filtered = filtered.filter((wine) => !wine.locationId);
      } else {
        // Wines with specific location
        filtered = filtered.filter((wine) => wine.locationId === filters.locationId);
      }
    }

    // Filter by ownership/sharing
    if (filters.ownership && filters.ownership !== 'all') {
      filtered = filtered.filter((wine) => {
        const isOwner = wine.ownerId === user?.id || wine.userId === user?.id;

        switch (filters.ownership) {
          case 'mine-only':
            // Solo vini miei non condivisi
            return isOwner && !wine.isShared;
          case 'mine-shared-full':
            // Vini miei condivisi con accesso completo (full)
            return isOwner && wine.isShared &&
                   wine.sharedWith?.some(share => share.type === 'full');
          case 'mine-shared-read-only':
            // Vini miei condivisi con accesso solo lettura (read-only)
            return isOwner && wine.isShared &&
                   wine.sharedWith?.some(share => share.type === 'read-only');
          case 'shared-with-me':
            // Vini condivisi con me da altri
            return !isOwner && wine.sharedWithUserIds?.includes(user?.id || '');
          default:
            return true;
        }
      });
    }

    // Sort wines - supporta sia nuovo sistema (sortField + sortDirection) che vecchio (sortBy)
    const sortField = filters.sortField;
    const sortDirection = filters.sortDirection || 'asc';
    const sortBy = filters.sortBy;

    if (sortField && sortDirection) {
      // Nuovo sistema: campo + direzione separati
      filtered.sort((a, b) => {
        let comparison = 0;

        switch (sortField) {
          case 'name':
            comparison = a.name.localeCompare(b.name);
            break;
          case 'date':
            comparison = (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0);
            break;
          case 'alcohol':
            comparison = (a.alcoholContent || 0) - (b.alcoholContent || 0);
            break;
          case 'vintage':
            comparison = (a.vintage || 0) - (b.vintage || 0);
            break;
          case 'rating':
            comparison = (a.rating || 0) - (b.rating || 0);
            break;
          case 'estimatedValue':
            comparison = (a.estimatedValue || 0) - (b.estimatedValue || 0);
            break;
          default:
            return 0;
        }

        return sortDirection === 'desc' ? -comparison : comparison;
      });
    } else if (sortBy) {
      // Sistema legacy (backward compatibility)
      filtered.sort((a, b) => {
        switch (sortBy) {
          case 'name-asc':
            return a.name.localeCompare(b.name);
          case 'name-desc':
            return b.name.localeCompare(a.name);
          case 'date-newest':
            return (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0);
          case 'date-oldest':
            return (a.createdAt?.seconds ?? 0) - (b.createdAt?.seconds ?? 0);
          case 'alcohol-asc':
            return (a.alcoholContent || 0) - (b.alcoholContent || 0);
          case 'alcohol-desc':
            return (b.alcoholContent || 0) - (a.alcoholContent || 0);
          case 'vintage-newest':
            return (b.vintage || 0) - (a.vintage || 0);
          case 'vintage-oldest':
            return (a.vintage || 0) - (b.vintage || 0);
          case 'rating-high':
            return (b.rating || 0) - (a.rating || 0);
          case 'rating-low':
            return (a.rating || 0) - (b.rating || 0);
          case 'estimatedValue-asc':
            return (a.estimatedValue || 0) - (b.estimatedValue || 0);
          case 'estimatedValue-desc':
            return (b.estimatedValue || 0) - (a.estimatedValue || 0);
          default:
            return 0;
        }
      });
    }

    return filtered;
  };

  // ============================================================================
  // FUNZIONI PERMESSI (SHARING)
  // ============================================================================

  // Verifica se l'utente corrente è il proprietario del vino
  const isOwner = (wine: Wine): boolean => {
    if (!user) return false;
    return wine.ownerId === user.id || wine.userId === user.id;
  };

  // Verifica se l'utente può visualizzare il vino
  const canViewWine = (wine: Wine): boolean => {
    if (!user) return false;

    // Sono owner?
    if (isOwner(wine)) return true;

    // Condivisione (qualsiasi tipo)?
    return wine.sharedWith?.some(s => s.userId === user.id) ?? false;
  };

  // Verifica se l'utente può modificare il vino
  const canModifyWine = (wine: Wine): boolean => {
    if (!user) return false;

    // Sono owner?
    if (isOwner(wine)) return true;

    // Condivisione full access?
    const myAccess = wine.sharedWith?.find(s => s.userId === user.id);
    return myAccess?.type === 'full';
  };

  // Verifica se l'utente può eliminare il vino
  const canDeleteWine = (wine: Wine): boolean => {
    // Solo il proprietario può eliminare
    return isOwner(wine);
  };

  // ============================================================================
  // FUNZIONI CONDIVISIONE
  // ============================================================================

  // Condividi vini con un utente
  const shareWines = async (
    wineIds: string[],
    targetUserId: string,
    targetUserName: string,
    type: ShareAccessType
  ): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const batch = writeBatch(db);

      for (const wineId of wineIds) {
        const wine = wines.find(w => w.id === wineId);

        // Verifica che il vino esista e che l'utente sia il proprietario
        if (!wine || !isOwner(wine)) {
          throw new Error(`Non autorizzato a condividere il vino ${wineId}`);
        }

        // Verifica che il vino sia in cantina (non bevuto)
        if (wine.status !== 'cellar') {
          throw new Error('Puoi condividere solo vini in cantina');
        }

        // Verifica se il vino è già condiviso con questo utente
        const existingShare = wine.sharedWith?.find(s => s.userId === targetUserId);
        if (existingShare) {
          // Se già condiviso, aggiorna solo il tipo di accesso
          const updatedSharedWith = wine.sharedWith!.map(s =>
            s.userId === targetUserId
              ? { ...s, type, sharedAt: Timestamp.now() }
              : s
          );

          const wineRef = doc(db, 'wines', wineId);
          batch.update(wineRef, {
            sharedWith: updatedSharedWith,
            isShared: true,
            ownerName: user.displayName || user.email,
            fullAccessUserIds: updatedSharedWith
              .filter(s => s.type === 'full')
              .map(s => s.userId),
            updatedAt: serverTimestamp(),
          });
        } else {
          // Aggiungi nuova condivisione
          const newShare: SharedAccess = {
            userId: targetUserId,
            userName: targetUserName,
            type,
            sharedAt: Timestamp.now(),
            sharedBy: user.id,
          };

          const updatedSharedWith = [...(wine.sharedWith || []), newShare];
          const updatedSharedWithUserIds = [...(wine.sharedWithUserIds || []), targetUserId];

          const wineRef = doc(db, 'wines', wineId);
          batch.update(wineRef, {
            sharedWith: updatedSharedWith,
            sharedWithUserIds: updatedSharedWithUserIds,
            isShared: true,
            ownerName: user.displayName || user.email,
            fullAccessUserIds: updatedSharedWith
              .filter(s => s.type === 'full')
              .map(s => s.userId),
            updatedAt: serverTimestamp(),
          });
        }
      }

      await batch.commit();
    } catch (error) {
      console.error('Error sharing wines:', error);
      throw new Error('Errore durante la condivisione dei vini');
    }
  };

  // Rimuovi condivisione di un vino
  const unshareWine = async (wineId: string, targetUserId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const wine = wines.find(w => w.id === wineId);

      // Verifica che il vino esista e che l'utente sia il proprietario
      if (!wine || !isOwner(wine)) {
        throw new Error('Non autorizzato a modificare la condivisione di questo vino');
      }

      // Rimuovi l'utente da tutti gli array
      const updatedSharedWith = (wine.sharedWith || []).filter(s => s.userId !== targetUserId);
      const updatedSharedWithUserIds = (wine.sharedWithUserIds || []).filter(id => id !== targetUserId);
      const updatedFullAccessUserIds = (wine.fullAccessUserIds || []).filter(id => id !== targetUserId);

      const wineRef = doc(db, 'wines', wineId);
      await updateDoc(wineRef, {
        sharedWith: updatedSharedWith,
        sharedWithUserIds: updatedSharedWithUserIds,
        fullAccessUserIds: updatedFullAccessUserIds,
        isShared: updatedSharedWith.length > 0,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error unsharing wine:', error);
      throw new Error('Errore durante la rimozione della condivisione');
    }
  };

  // Modifica tipo di accesso condivisione (da full a read-only o viceversa)
  const updateShareAccessType = async (wineId: string, targetUserId: string, newType: ShareAccessType): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const wine = wines.find(w => w.id === wineId);

      // Verifica che il vino esista e che l'utente sia il proprietario
      if (!wine || !isOwner(wine)) {
        throw new Error('Non autorizzato a modificare la condivisione di questo vino');
      }

      // Trova e aggiorna il tipo di accesso per l'utente target
      const updatedSharedWith = (wine.sharedWith || []).map(share =>
        share.userId === targetUserId
          ? { ...share, type: newType }
          : share
      );

      // Aggiorna fullAccessUserIds in base al nuovo tipo
      let updatedFullAccessUserIds = wine.fullAccessUserIds || [];
      if (newType === 'full') {
        // Aggiungi a fullAccessUserIds se non è già presente
        if (!updatedFullAccessUserIds.includes(targetUserId)) {
          updatedFullAccessUserIds = [...updatedFullAccessUserIds, targetUserId];
        }
      } else {
        // Rimuovi da fullAccessUserIds se era presente
        updatedFullAccessUserIds = updatedFullAccessUserIds.filter(id => id !== targetUserId);
      }

      const wineRef = doc(db, 'wines', wineId);
      await updateDoc(wineRef, {
        sharedWith: updatedSharedWith,
        fullAccessUserIds: updatedFullAccessUserIds,
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error updating share access type:', error);
      throw new Error('Errore durante la modifica del tipo di accesso');
    }
  };

  // ============================================================================
  // NUOVE FUNZIONI CONNESSIONI (Sistema basato su connessioni permanenti)
  // ============================================================================

  /**
   * Genera codice invito per connettere un nuovo utente
   * Crea un documento UserConnection con status 'pending' e codice random
   * L'email verrà inviata automaticamente da Firebase Function
   */
  const generateConnectionCode = async (inviteeEmail: string): Promise<string> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Normalizza email
      const normalizedEmail = inviteeEmail.trim().toLowerCase();

      // Verifica che non stia invitando se stesso
      if (normalizedEmail === user.email.toLowerCase()) {
        throw new Error('Non puoi invitare te stesso');
      }

      // Verifica se esiste già una connessione (pending o accepted) con questo email
      const existingConnectionsQuery = query(
        collection(db, 'userConnections'),
        where('inviteeEmail', '==', normalizedEmail),
        where('userA', '==', user.id)
      );

      const existingSnapshot = await getDocs(existingConnectionsQuery);

      if (!existingSnapshot.empty) {
        const existing = existingSnapshot.docs[0].data();
        if (existing.status === 'accepted') {
          throw new Error('Sei già connesso con questo utente');
        }
        if (existing.status === 'pending') {
          throw new Error('Hai già un invito pendente per questo utente');
        }
      }

      // Genera codice casuale (8 caratteri alfanumerici)
      const generateCode = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let code = '';
        for (let i = 0; i < 8; i++) {
          code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
      };

      const code = generateCode();

      // Calcola scadenza (7 giorni)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      // Crea documento userConnection
      const connectionRef = doc(collection(db, 'userConnections'));
      const connectionData = {
        userA: user.id,
        userB: '', // Verrà popolato quando accetta
        userAName: user.displayName || user.email,
        userAEmail: user.email,
        // userBName e userBEmail verranno aggiunti quando l'utente accetta
        status: 'pending',
        initiatedBy: user.id,
        code,
        inviteeEmail: normalizedEmail,
        createdAt: serverTimestamp(),
        expiresAt: expiresAt,
      };

      await setDoc(connectionRef, connectionData);

      console.log(`Connection invitation created with code: ${code}`);
      return code;
    } catch (error) {
      console.error('Error generating connection code:', error);
      throw error;
    }
  };

  /**
   * Accetta un codice invito e crea la connessione tra i due utenti
   */
  const acceptConnectionCode = async (code: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Cerca il codice nei userConnections
      const connectionsQuery = query(
        collection(db, 'userConnections'),
        where('code', '==', code.toUpperCase()),
        where('status', '==', 'pending')
      );

      const snapshot = await getDocs(connectionsQuery);

      if (snapshot.empty) {
        throw new Error('Codice non valido o già utilizzato');
      }

      const connectionDoc = snapshot.docs[0];
      const connection = { id: connectionDoc.id, ...connectionDoc.data() } as UserConnection;

      // Verifica scadenza
      const now = new Date();
      const expiresAt = connection.expiresAt && typeof connection.expiresAt.toDate === 'function'
        ? connection.expiresAt.toDate()
        : new Date(connection.expiresAt as any);

      if (expiresAt < now) {
        throw new Error('Codice scaduto');
      }

      // Verifica che non stia accettando il proprio invito
      if (connection.userA === user.id) {
        throw new Error('Non puoi accettare il tuo stesso invito');
      }

      // Verifica che l'email corrisponda (opzionale, per sicurezza)
      if (connection.inviteeEmail.toLowerCase() !== user.email.toLowerCase()) {
        throw new Error('Questo invito non è per te');
      }

      // Aggiorna la connessione
      await updateDoc(doc(db, 'userConnections', connection.id), {
        status: 'accepted',
        userB: user.id,
        userBName: user.displayName || user.email,
        userBEmail: user.email,
        acceptedAt: serverTimestamp(),
      });

      console.log(`Connection accepted: ${connection.userAName} <-> ${user.displayName}`);
    } catch (error) {
      console.error('Error accepting connection code:', error);
      throw error;
    }
  };

  /**
   * Accetta un invito direttamente tramite connectionId (senza dover inserire il codice)
   */
  const acceptInvite = async (connectionId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Trova l'invito
      const invite = receivedInvites.find(inv => inv.id === connectionId);
      if (!invite) {
        throw new Error('Invito non trovato');
      }

      // Verifica scadenza
      const now = new Date();
      const expiresAt = invite.expiresAt && typeof invite.expiresAt.toDate === 'function'
        ? invite.expiresAt.toDate()
        : new Date(invite.expiresAt as any);

      if (expiresAt < now) {
        throw new Error('Invito scaduto');
      }

      // Verifica che l'email corrisponda
      if (invite.inviteeEmail.toLowerCase() !== user.email.toLowerCase()) {
        throw new Error('Questo invito non è per te');
      }

      // Aggiorna la connessione
      await updateDoc(doc(db, 'userConnections', connectionId), {
        status: 'accepted',
        userB: user.id,
        userBName: user.displayName || user.email,
        userBEmail: user.email,
        acceptedAt: serverTimestamp(),
      });

      console.log(`Invite accepted: ${invite.userAName} <-> ${user.displayName}`);
    } catch (error) {
      console.error('Error accepting invite:', error);
      throw error;
    }
  };

  /**
   * Rifiuta un invito ricevuto
   */
  const rejectInvite = async (connectionId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Trova l'invito
      const invite = receivedInvites.find(inv => inv.id === connectionId);
      if (!invite) {
        throw new Error('Invito non trovato');
      }

      // Verifica che l'email corrisponda
      if (invite.inviteeEmail.toLowerCase() !== user.email.toLowerCase()) {
        throw new Error('Questo invito non è per te');
      }

      // Elimina l'invito
      await deleteDoc(doc(db, 'userConnections', connectionId));

      console.log(`Invite rejected from: ${invite.userAName}`);
    } catch (error) {
      console.error('Error rejecting invite:', error);
      throw error;
    }
  };

  /**
   * Cancella un invito inviato
   */
  const cancelInvite = async (connectionId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Trova l'invito
      const invite = sentInvites.find(inv => inv.id === connectionId);
      if (!invite) {
        throw new Error('Invito non trovato');
      }

      // Verifica che l'utente sia il creatore
      if (invite.userA !== user.id) {
        throw new Error('Non autorizzato a cancellare questo invito');
      }

      // Elimina l'invito
      await deleteDoc(doc(db, 'userConnections', connectionId));

      console.log(`Invite cancelled to: ${invite.inviteeEmail}`);
    } catch (error) {
      console.error('Error cancelling invite:', error);
      throw error;
    }
  };

  /**
   * Rimuove una connessione esistente
   * Rimuove anche tutte le condivisioni di vini tra i due utenti
   */
  const removeConnection = async (connectionId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const connection = connectedUsers.find(c => c.id === connectionId);
      if (!connection) {
        throw new Error('Connessione non trovata');
      }

      // Verifica che l'utente sia parte della connessione
      if (connection.userA !== user.id && connection.userB !== user.id) {
        throw new Error('Non autorizzato a rimuovere questa connessione');
      }

      // Trova l'altro utente nella connessione
      const otherUserId = connection.userA === user.id ? connection.userB : connection.userA;

      // Rimuovi tutte le condivisioni con questo utente
      const batch = writeBatch(db);

      // Rimuovi condivisioni dai miei vini
      const myWines = wines.filter(w => isOwner(w) && w.sharedWithUserIds?.includes(otherUserId));
      for (const wine of myWines) {
        const updatedSharedWith = wine.sharedWith?.filter(s => s.userId !== otherUserId) || [];
        const updatedSharedWithUserIds = wine.sharedWithUserIds?.filter(id => id !== otherUserId) || [];
        const updatedFullAccessUserIds = wine.fullAccessUserIds?.filter(id => id !== otherUserId) || [];

        batch.update(doc(db, 'wines', wine.id), {
          sharedWith: updatedSharedWith,
          sharedWithUserIds: updatedSharedWithUserIds,
          fullAccessUserIds: updatedFullAccessUserIds,
          isShared: updatedSharedWith.length > 0,
        });
      }

      // Rimuovi condivisioni dai vini dell'altro utente condivisi con me
      const theirWinesSharedWithMe = wines.filter(w =>
        (w.ownerId === otherUserId || w.userId === otherUserId) &&
        w.sharedWithUserIds?.includes(user.id)
      );

      for (const wine of theirWinesSharedWithMe) {
        const updatedSharedWith = wine.sharedWith?.filter(s => s.userId !== user.id) || [];
        const updatedSharedWithUserIds = wine.sharedWithUserIds?.filter(id => id !== user.id) || [];
        const updatedFullAccessUserIds = wine.fullAccessUserIds?.filter(id => id !== user.id) || [];

        batch.update(doc(db, 'wines', wine.id), {
          sharedWith: updatedSharedWith,
          sharedWithUserIds: updatedSharedWithUserIds,
          fullAccessUserIds: updatedFullAccessUserIds,
          isShared: updatedSharedWith.length > 0,
        });
      }

      // Elimina la connessione
      batch.delete(doc(db, 'userConnections', connectionId));

      await batch.commit();

      console.log(`Connection removed: ${connectionId}`);
    } catch (error) {
      console.error('Error removing connection:', error);
      throw error;
    }
  };

  /**
   * Condivide vini con utenti connessi
   * Sostituisce il vecchio sistema con codici usa-e-getta
   */
  const shareWinesWithUsers = async (
    wineIds: string[],
    userIds: string[],
    type: ShareAccessType
  ): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Verifica che l'utente sia owner di tutti i vini
      for (const wineId of wineIds) {
        const wine = wines.find(w => w.id === wineId);
        if (!wine || !isOwner(wine)) {
          throw new Error(`Non autorizzato a condividere il vino ${wineId}`);
        }
        if (wine.status !== 'cellar') {
          throw new Error('Puoi condividere solo vini in cantina');
        }
      }

      // Verifica che tutti gli userIds siano utenti connessi
      for (const userId of userIds) {
        const isConnected = connectedUsers.some(
          c => (c.userA === userId || c.userB === userId) && c.status === 'accepted'
        );

        if (!isConnected) {
          throw new Error(`Utente ${userId} non è connesso`);
        }
      }

      // Batch update dei vini
      const batch = writeBatch(db);

      for (const wineId of wineIds) {
        const wine = wines.find(w => w.id === wineId);
        if (!wine) continue;

        const updatedSharedWith = [...(wine.sharedWith || [])];
        const updatedSharedWithUserIds = [...(wine.sharedWithUserIds || [])];

        // Aggiungi nuovi utenti
        for (const userId of userIds) {
          // Verifica se già condiviso
          if (updatedSharedWithUserIds.includes(userId)) {
            // Aggiorna il tipo di accesso se già condiviso
            const index = updatedSharedWith.findIndex(s => s.userId === userId);
            if (index !== -1) {
              updatedSharedWith[index].type = type;
            }
          } else {
            // Trova nome utente dalla connessione
            const connection = connectedUsers.find(
              c => (c.userA === userId || c.userB === userId) && c.status === 'accepted'
            );

            const userName = connection
              ? (connection.userA === userId ? connection.userAName : connection.userBName) || 'Unknown User'
              : 'Unknown User';

            updatedSharedWith.push({
              userId,
              userName,
              type,
              sharedAt: Timestamp.now(),
              sharedBy: user.id,
            });

            updatedSharedWithUserIds.push(userId);
          }
        }

        batch.update(doc(db, 'wines', wineId), {
          sharedWith: updatedSharedWith,
          sharedWithUserIds: updatedSharedWithUserIds,
          fullAccessUserIds: updatedSharedWith
            .filter(s => s.type === 'full')
            .map(s => s.userId),
          isShared: true,
          ownerName: user.displayName || user.email,
        });
      }

      await batch.commit();

      console.log(`Shared ${wineIds.length} wines with ${userIds.length} users`);
    } catch (error) {
      console.error('Error sharing wines with users:', error);
      throw error;
    }
  };

  // ============================================================================
  // FUNZIONI CONDIVISIONE DEPRECATE (Vecchio sistema con codici usa-e-getta)
  // ============================================================================

  // Genera codice condivisione (DEPRECATED)
  const generateShareCode = async (wineIds: string[], type: ShareAccessType): Promise<string> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Verifica che l'utente sia owner di tutti i vini
      for (const wineId of wineIds) {
        const wine = wines.find(w => w.id === wineId);
        if (!wine || !isOwner(wine)) {
          throw new Error(`Non autorizzato a condividere il vino ${wineId}`);
        }
        if (wine.status !== 'cellar') {
          throw new Error('Puoi condividere solo vini in cantina');
        }
      }

      // Genera codice casuale (8 caratteri alfanumerici)
      const generateCode = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let code = '';
        for (let i = 0; i < 8; i++) {
          code += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return code;
      };

      const code = generateCode();

      // Calcola scadenza (7 giorni)
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      // Ottieni nomi vini per UI
      const wineNames = wineIds.map(id => wines.find(w => w.id === id)?.name || '').filter(Boolean);

      // Crea ShareRequest
      const shareRequestRef = doc(collection(db, 'shareRequests'));
      const shareRequest: Omit<ShareRequest, 'id'> = {
        requestedBy: user.id,
        wineIds,
        wineNames,
        type,
        code,
        expiresAt: serverTimestamp() as any,
        status: 'pending',
        createdAt: serverTimestamp() as any,
      };

      await setDoc(shareRequestRef, shareRequest);

      return code;
    } catch (error) {
      console.error('Error generating share code:', error);
      throw new Error('Errore durante la generazione del codice');
    }
  };

  // Accetta codice condivisione
  const acceptShareCode = async (code: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Cerca ShareRequest con questo codice
      const shareRequestsRef = collection(db, 'shareRequests');
      const q = query(shareRequestsRef, where('code', '==', code), where('status', '==', 'pending'));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        throw new Error('Codice non valido o già utilizzato');
      }

      const shareRequestDoc = snapshot.docs[0];
      const shareRequest = { id: shareRequestDoc.id, ...shareRequestDoc.data() } as ShareRequest;

      // Verifica scadenza
      const now = new Date();
      const expiresAt = shareRequest.expiresAt && typeof shareRequest.expiresAt.toDate === 'function'
        ? shareRequest.expiresAt.toDate()
        : new Date(shareRequest.expiresAt as any);
      if (expiresAt < now) {
        throw new Error('Codice scaduto');
      }

      // Non puoi accettare la condivisione dei tuoi stessi vini
      if (shareRequest.requestedBy === user.id) {
        throw new Error('Non puoi accettare la condivisione dei tuoi vini');
      }

      // Aggiungi condivisione a tutti i vini
      const batch = writeBatch(db);

      for (const wineId of shareRequest.wineIds) {
        // Il vino potrebbe non essere visibile all'utente corrente prima della condivisione
        // Quindi recuperiamolo direttamente da Firestore
        const wineRef = doc(db, 'wines', wineId);
        const wineDoc = await getDoc(wineRef);

        if (!wineDoc.exists()) {
          console.warn(`Wine ${wineId} not found, skipping`);
          continue;
        }

        const wineData = wineDoc.data() as Wine;

        // Verifica che sia ancora in cantina
        if (wineData.status !== 'cellar') {
          console.warn(`Wine ${wineId} is not in cellar, skipping`);
          continue;
        }

        const newShare: SharedAccess = {
          userId: user.id,
          userName: user.displayName,
          type: shareRequest.type,
          sharedAt: Timestamp.now(),
          sharedBy: shareRequest.requestedBy,
        };

        const updatedSharedWith = [...(wineData.sharedWith || []), newShare];
        const updatedSharedWithUserIds = [...(wineData.sharedWithUserIds || []), user.id];

        batch.update(wineRef, {
          sharedWith: updatedSharedWith,
          sharedWithUserIds: updatedSharedWithUserIds,
          fullAccessUserIds: updatedSharedWith
            .filter(s => s.type === 'full')
            .map(s => s.userId),
          isShared: true,
          updatedAt: serverTimestamp(),
        });
      }

      // Marca ShareRequest come accettato
      batch.update(doc(db, 'shareRequests', shareRequestDoc.id), {
        status: 'accepted',
        acceptedBy: user.id,
        acceptedAt: serverTimestamp(),
      });

      await batch.commit();
    } catch (error) {
      console.error('Error accepting share code:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Errore durante l\'accettazione del codice');
    }
  };

  // ==================== GESTIONE LOCAZIONI ====================

  // Crea nuova locazione
  const createLocation = async (name: string, description?: string): Promise<string> => {
    if (!user) throw new Error('User not authenticated');

    try {
      // Valida limite massimo 10 locazioni
      if (locations.length >= 10) {
        throw new Error('Limite massimo di 10 locazioni raggiunto');
      }

      // Verifica duplicati (case-insensitive)
      const duplicateName = locations.some(
        loc => loc.name.toLowerCase() === name.trim().toLowerCase()
      );
      if (duplicateName) {
        throw new Error('Esiste già una locazione con questo nome');
      }

      const locationRef = doc(collection(db, 'wineLocations'));
      const locationData = {
        userId: user.id,
        name: name.trim(),
        description: description?.trim() || '',
        order: locations.length, // Aggiungi alla fine
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(locationRef, locationData);
      return locationRef.id;
    } catch (error) {
      console.error('Error creating location:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Errore durante la creazione della locazione');
    }
  };

  // Aggiorna locazione
  const updateLocation = async (
    locationId: string,
    data: Partial<Pick<WineLocation, 'name' | 'description' | 'order'>>
  ): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const location = locations.find(loc => loc.id === locationId);
      if (!location) {
        throw new Error('Locazione non trovata');
      }
      if (location.userId !== user.id) {
        throw new Error('Non autorizzato');
      }

      // Verifica duplicati se si sta cambiando il nome
      if (data.name) {
        const duplicateName = locations.some(
          loc => loc.id !== locationId &&
            loc.name.toLowerCase() === data.name!.trim().toLowerCase()
        );
        if (duplicateName) {
          throw new Error('Esiste già una locazione con questo nome');
        }
      }

      const updateData: any = {
        updatedAt: serverTimestamp(),
      };

      if (data.name !== undefined) {
        updateData.name = data.name.trim();
      }
      if (data.description !== undefined) {
        updateData.description = data.description.trim();
      }
      if (data.order !== undefined) {
        updateData.order = data.order;
      }

      await updateDoc(doc(db, 'wineLocations', locationId), updateData);
    } catch (error) {
      console.error('Error updating location:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Errore durante l\'aggiornamento della locazione');
    }
  };

  // Elimina locazione
  const deleteLocation = async (locationId: string): Promise<void> => {
    if (!user) throw new Error('User not authenticated');

    try {
      const location = locations.find(loc => loc.id === locationId);
      if (!location) {
        throw new Error('Posizione non trovata');
      }
      if (location.userId !== user.id) {
        throw new Error('Non autorizzato');
      }

      // Trova vini che usano questa posizione e rimuovi il riferimento
      const winesUsingLocation = wines.filter(w => w.locationId === locationId);

      if (winesUsingLocation.length > 0) {
        // Usa batch per aggiornare tutti i vini contemporaneamente
        const batch = writeBatch(db);

        winesUsingLocation.forEach(wine => {
          const wineRef = doc(db, 'wines', wine.id);
          batch.update(wineRef, {
            locationId: null,
            updatedAt: serverTimestamp(),
          });
        });

        await batch.commit();
      }

      // Elimina la posizione
      await deleteDoc(doc(db, 'wineLocations', locationId));
    } catch (error) {
      console.error('Error deleting location:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Errore durante l\'eliminazione della posizione');
    }
  };

  const value: WinesContextType = {
    wines,
    loading,
    error,
    addWine,
    updateWine,
    deleteWine,
    markAsConsumed,
    incrementWineQuantity,
    identifyWineFromPhoto,
    identifyWineFromPhotoUrls,
    getWineStats,
    filterWines,
    // Funzioni connessioni (nuovo sistema)
    connectedUsers,
    sentInvites,
    receivedInvites,
    generateConnectionCode,
    acceptConnectionCode,
    acceptInvite,
    rejectInvite,
    cancelInvite,
    removeConnection,
    shareWinesWithUsers,
    // Funzioni condivisione (vecchio sistema - DEPRECATED)
    shareWines,
    unshareWine,
    updateShareAccessType,
    generateShareCode,
    acceptShareCode,
    // Funzioni permessi
    canModifyWine,
    canDeleteWine,
    canViewWine,
    isOwner,
    // Gestione locazioni
    locations,
    locationsLoading,
    createLocation,
    updateLocation,
    deleteLocation,
  };

  return <WinesContext.Provider value={value}>{children}</WinesContext.Provider>;
};
